import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';

function formatCompactNumber(value) {
    if (typeof value !== 'number') return value;
    if (value >= 1e6) return (value / 1e6).toFixed(1) + 'M';
    if (value >= 1e3) return (value / 1e3).toFixed(0) + 'k';
    return String(value);
}

// Grouped (side-by-side) bar chart for comparing multiple series per category
export default function SideBySideBarChart({ rows = [], xKey, yAxisKeys = [], theme = {}, onExposeLegend }) {
    const isHorizontal = theme?.orientation === 'horizontal';
    const maxLen = Number(theme?.categoryMaxLen) > 0 ? Number(theme.categoryMaxLen) : 26;
    const rawCategories = useMemo(() => Array.isArray(rows) ? rows.map(r => r?.[xKey]) : [], [rows, xKey]);
    const categories = useMemo(() => rawCategories.map(n => typeof n === 'string' ? (n.length > maxLen ? n.slice(0, maxLen - 1) + '…' : n) : n), [rawCategories, maxLen]);

    const series = useMemo(() => {
        const keys = Array.isArray(yAxisKeys) && yAxisKeys.length > 0 ? yAxisKeys : [];
        return keys.map(key => ({
            name: String(key),
            data: categories.map((_, i) => {
                const row = rows[i] || {};
                const v = Number(row?.[key]);
                return Number.isFinite(v) ? v : 0;
            })
        }));
    }, [rows, yAxisKeys, categories]);

    const colors = theme.seriesColors || ['#4F46E5', '#F59E0B', '#10B981', '#EF4444', '#6366F1', '#06B6D4'];

    const options = useMemo(() => ({
        chart: { stacked: false, parentHeightOffset: 0, toolbar: { show: false } },
        plotOptions: { bar: { horizontal: isHorizontal, borderRadius: 6, columnWidth: '45%', dataLabels: { position: isHorizontal ? 'right' : 'top' } } },
        legend: {
            show: theme?.legend === false ? false : (theme?.legend === true ? true : (typeof theme?.legend?.show === 'boolean' ? theme.legend.show : true)),
            position: typeof theme?.legend?.position === 'string' ? theme.legend.position : 'top',
            horizontalAlign: typeof theme?.legend?.horizontalAlign === 'string' ? theme.legend.horizontalAlign : 'left'
        },
        tooltip: { enabled: true },
        dataLabels: {
            enabled: theme?.showBarValues !== false,
            offsetY: isHorizontal ? 0 : -10,
            offsetX: isHorizontal ? 12 : 0,
            formatter: val => `${formatCompactNumber(val)}`,
            style: { fontWeight: 500, colors: ['#94a3b8'], fontSize: '12px' }
        },
        colors,
        grid: { show: Boolean(theme?.showGridX || theme?.showGridY), xaxis: { lines: { show: Boolean(theme?.showGridX) } }, yaxis: { lines: { show: Boolean(theme?.showGridY) } }, padding: isHorizontal ? { top: (theme?.showBarValues !== false ? 16 : 12), left: 32, right: 8, bottom: 0 } : { top: (theme?.showBarValues !== false ? 22 : 12), left: 0, right: 0, bottom: -8 } },
        xaxis: isHorizontal ? {
            title: { text: theme.xAxisTitle || undefined, style: { color: '#9ca3af', fontSize: '12px', fontWeight: 500 } },
            axisTicks: { show: false },
            axisBorder: { show: Boolean(theme?.showXAxisLine) },
            labels: { show: theme?.showXAxisLabels !== false, formatter: val => `${formatCompactNumber(Number(val))}`, style: { colors: '#9ca3af', fontSize: '12px' } }
        } : {
            categories,
            title: { text: theme.xAxisTitle || undefined, style: { color: '#9ca3af', fontSize: '12px', fontWeight: 500 } },
            axisTicks: { show: false },
            axisBorder: { show: Boolean(theme?.showXAxisLine) },
            labels: { show: theme?.showXAxisLabels !== false, rotate: -35, trim: false, hideOverlappingLabels: false, style: { colors: '#9ca3af', fontSize: '12px' } }
        },
        yaxis: isHorizontal ? {
            categories,
            title: { text: theme.yAxisTitle || undefined, style: { color: '#9ca3af', fontSize: '12px', fontWeight: 500 } },
            axisTicks: { show: false },
            axisBorder: { show: Boolean(theme?.showYAxisLine) },
            labels: { show: theme?.showYAxisLabels !== false, offsetX: 0, maxWidth: 220, style: { colors: '#9ca3af', fontSize: '12px' } }
        } : {
            axisTicks: { show: false },
            axisBorder: { show: Boolean(theme?.showYAxisLine) },
            labels: { show: theme?.showYAxisLabels !== false, formatter: val => `${formatCompactNumber(val)}`, style: { colors: '#9ca3af', fontSize: '12px' } }
        },
        states: { hover: { filter: { type: 'none' } }, active: { filter: { type: 'none' } } }
    }), [categories, colors, isHorizontal, theme]);

    // Expose legend items after render commit
    const exposedItems = useMemo(() => (yAxisKeys || []).map((key, i) => ({ label: String(key), color: colors?.[i] })), [yAxisKeys, colors]);
    React.useEffect(() => {
        if (typeof onExposeLegend === 'function') {
            onExposeLegend(exposedItems);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(exposedItems)]);

    return (
        <Chart
            type="bar"
            height={300}
            series={series}
            options={options}
        />
    );
}


